import { cn } from "@/utils/cn";
import { cva } from "class-variance-authority";
import {
    CheckCircleIcon,
    ExclamationCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";

const calloutVariants = cva("flex border [&>svg]:shrink-0", {
    variants: {
        variant: {
            primary:
                "border-darkgreen-200 bg-darkgreen-500/40 text-darkgreen-100",
            secondary: "border-green-400 bg-green-700/40 text-green-25",
            success: "border-emerald-700 bg-emerald-950-40 text-emerald-25",
            danger: "border-red-300 bg-red-600-40 text-red-100",
            warning: "border-orange-500 bg-orange-800-40 text-orange-100",
            info: "border-blue-600 bg-blue-900-40 text-blue-25",
            light: "border-neutral-500 bg-neutral-200/40 text-neutral-600",
        },
        size: {
            default:
                "gap-2 rounded-3xl p-2 text-base [&_svg]:size-6 [&_svg]:stroke-2",
            xs: "gap-2 rounded-3xl p-1 text-sm [&_svg]:size-5 [&_svg]:stroke-2",
            sm: "gap-2 rounded-3xl p-1.5 text-sm [&_svg]:size-5 [&_svg]:stroke-2",
            lg: "[&_svg]:stroke-2.5 gap-3 rounded-3xl p-3 text-lg [&_svg]:size-7",
        },
    },
    defaultVariants: {
        variant: "primary",
        size: "default",
    },
});

const iconTypes = [
    { variant: "success", component: <CheckCircleIcon /> },
    { variant: "danger", component: <ExclamationCircleIcon /> },
    { variant: "warning", component: <ExclamationTriangleIcon /> },
    { variant: "info", component: <InformationCircleIcon /> },
];

const iconMap = Object.fromEntries(
    iconTypes.map((o) => [o.variant, o.component]),
);

export const Callout = ({
    children,
    className,
    variant,
    size,
    icon = false,
    dismiss = false,
    onClose,
    ...props
}) => {
    return (
        <div
            className={cn(calloutVariants({ className, variant, size }))}
            {...props}
        >
            <div className="flex items-baseline py-2 ps-2 pe-0">
                {icon && (iconMap[variant] ?? "")}
            </div>
            <div className="flex-1 py-2 pe-3">{children}</div>
            {dismiss && (
                <button
                    type="button"
                    className="-m-2 cursor-pointer p-2 opacity-75 hover:opacity-100"
                    aria-label="Close"
                >
                    <XMarkIcon onClick={onClose} />
                </button>
            )}
        </div>
    );
};
